'use client';

import { ComponentProps, FocusEvent, MouseEvent, useCallback, useRef } from 'react';
import clsx from 'clsx';
import { useRouter } from 'next/navigation';

type NonIndexableLinkProps = ComponentProps<'a'> & {
  href: string;
  prefetch?: boolean;
  scroll?: boolean;
};

/**
 * A link component that prevents search engines from indexing the linked page.
 * It uses client-side navigation provided by Next.js and sets the href
 * attribute on focus events to allow the right-click context menu functionality.
 * See: DEV-225356
 */
export const NonIndexableLink = ({
  children,
  className,
  href,
  onClick,
  onFocus,
  scroll,
  // The prop only exists to have the same interface as the Next.js Link
  prefetch: _,
  ...props
}: NonIndexableLinkProps) => {
  const linkRef = useRef<HTMLAnchorElement>(null);
  const router = useRouter();

  const handleClick = useCallback(
    (event: MouseEvent<HTMLAnchorElement>) => {
      onClick?.(event);

      // The handler may navigate on its own (e.g. inside a transition that also updates optimistic
      // state) and signals that by preventing the default — don't navigate a second time.
      if (event.defaultPrevented) {
        return;
      }

      // Only handle left clicks without modifier keys, like Ctrl or Command
      if (event.ctrlKey || event.metaKey) {
        return;
      }

      event.preventDefault();
      router.push(href, { scroll });
    },
    [href, onClick, router, scroll]
  );

  const handleFocus = useCallback(
    (event: FocusEvent<HTMLAnchorElement>) => {
      onFocus?.(event);

      // We set the href attribute on focus to allow the right-click context menu and keyboard navigation
      if (linkRef.current) {
        linkRef.current.href = href;
      }
    },
    [href, onFocus]
  );

  return (
    <a
      {...props}
      tabIndex={0}
      className={clsx('cursor-pointer', className)}
      ref={linkRef}
      href={undefined}
      onClick={handleClick}
      onFocus={handleFocus}
      data-no-index
    >
      {children}
    </a>
  );
};
